// --- START: Locale-aware formatting utilities ---
import i18n from '@/i18n/config';

/**
 * Format a date according to the current locale
 * For pt-PT: "10 Jan 2025, 14:25" (abbreviated months: Jan, Fev, Mar, Abr, Mai, Jun, Jul, Ago, Set, Out, Nov, Dez)
 * For en: "Dec 10, 2025, 2:25 PM"
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  
  const locale = i18n.language || 'en';
  
  // For Portuguese, use custom format with abbreviated month
  if (locale === 'pt-PT') {
    const day = dateObj.getDate();
    let month = dateObj.toLocaleDateString('pt-PT', { month: 'short' });
    // Capitalize and remove period (jan. -> Jan, fev. -> Fev, etc.)
    month = month.charAt(0).toUpperCase() + month.slice(1).replace('.', '');
    const year = dateObj.getFullYear();
    const time = dateObj.toLocaleTimeString('pt-PT', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    return `${day} ${month} ${year}, ${time}`;
  }
  
  // For English, use standard format
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format a date without time
 * For pt-PT: "10 Jan 2025"
 * For en: "Dec 10, 2025"
 */
export function formatDateShort(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  
  const locale = i18n.language || 'en';
  
  if (locale === 'pt-PT') {
    const day = dateObj.getDate();
    let month = dateObj.toLocaleDateString('pt-PT', { month: 'short' });
    // Capitalize and remove period (jan. -> Jan, fev. -> Fev, etc.)
    month = month.charAt(0).toUpperCase() + month.slice(1).replace('.', '');
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  }
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format a date for "Member since" type displays
 * For pt-PT: "Dezembro 2025"
 * For en: "December 2025"
 */
export function formatMemberSince(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  
  const locale = i18n.language || 'en';
  
  return dateObj.toLocaleDateString(locale === 'pt-PT' ? 'pt-PT' : 'en-US', {
    year: 'numeric',
    month: 'long'
  });
}

/**
 * Format currency with proper locale formatting
 * Always uses € symbol
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '€0.00';
  
  const locale = i18n.language || 'en';
  
  return new Intl.NumberFormat(locale === 'pt-PT' ? 'pt-PT' : 'en-US', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
}

/**
 * Format a number with proper locale formatting
 */
export function formatNumber(num: number | null | undefined, decimals: number = 0): string {
  if (num === null || num === undefined) return '0';
  
  const locale = i18n.language || 'en';
  
  return new Intl.NumberFormat(locale === 'pt-PT' ? 'pt-PT' : 'en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
}
// --- END: Locale-aware formatting utilities ---
